import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, MonitorUp, RefreshCw, VideoOff, Volume2 } from "lucide-react";
import { useSpeaking } from "../hooks/useSpeaking";

interface Props {
  stream: MediaStream | null;
  name: string;
  /** Self-view and screen previews must be muted to avoid echo. */
  muted?: boolean;
  mirrored?: boolean;
  isScreen?: boolean;
  /** Whether the participant's mic is ON (unmuted). Screen tiles omit it. */
  micOn?: boolean;
  cameraOff?: boolean;
  reconnecting?: boolean;
  failed?: boolean;
  sinkId?: string | null;
  spotlight?: boolean;
  /** Persist a playback-level diagnostic event (autoplay block, sink failure). */
  onDiagnostic?: (type: string, payload?: Record<string, unknown>) => void;
}

export function VideoTile({
  stream,
  name,
  muted = false,
  mirrored = false,
  isScreen = false,
  micOn,
  cameraOff = false,
  reconnecting = false,
  failed = false,
  sinkId = null,
  spotlight = false,
  onDiagnostic,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Autoplay with sound is a POLICY decision the browser makes silently — a
  // blocked play() means the participant sees video (or an avatar) and hears
  // NOTHING, with no error anywhere. Verify playback explicitly and fall back
  // to an overlay whose click (a user gesture) retries play().
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  // Analyse the stream's audio for live speech — only when the mic is on and
  // this isn't a screen tile (screen has no relevant audio here).
  const speaking = useSpeaking(stream, !isScreen && micOn === true);
  const showMic = !isScreen && micOn !== undefined;

  const attemptPlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || !el.srcObject) return;
    const p = el.play();
    if (!p || typeof p.then !== "function") return;
    p.then(() => {
      setPlaybackBlocked((was) => {
        if (was) onDiagnostic?.("playback_resumed");
        return false;
      });
    }).catch((err: DOMException) => {
      // AbortError = a newer srcObject/load interrupted this play() — benign.
      if (err?.name === "AbortError") return;
      setPlaybackBlocked(true);
      onDiagnostic?.("playback_blocked", { error: err?.name ?? String(err) });
    });
  }, [onDiagnostic]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (stream) attemptPlay();
    else setPlaybackBlocked(false);
  }, [stream, attemptPlay]);

  // Last sink actually applied to the element — Chrome keeps an explicit sink
  // forever, so returning the pref to "Domyślna" (sinkId=null) must actively
  // reset it, and a chosen device vanishing mid-call must fall back, or the
  // incoming audio routes to a device that no longer exists.
  const appliedSinkRef = useRef<string | null>(null);
  useEffect(() => {
    const el = videoRef.current as
      | (HTMLVideoElement & { setSinkId?: (id: string) => Promise<void> })
      | null;
    if (!el || muted || typeof el.setSinkId !== "function") return;
    const apply = (id: string, via: string) =>
      el
        .setSinkId(id)
        .then(() => {
          appliedSinkRef.current = id || null;
        })
        .catch((err) => {
          onDiagnostic?.("sink_error", {
            via,
            sink: id,
            error: err instanceof DOMException ? err.name : String(err),
          });
          if (id) {
            // Vanished device → route to the default output instead of nowhere.
            void el.setSinkId("").then(() => {
              appliedSinkRef.current = null;
              onDiagnostic?.("sink_fallback_default", { via, sink: id });
            }).catch(() => {});
          }
        });
    if (!sinkId) {
      if (appliedSinkRef.current) void apply("", "pref");
      return;
    }
    void apply(sinkId, "pref");
    // Re-validate after plug/unplug: an explicitly-sinked element does NOT
    // fall back by itself when its device disappears.
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    const onDeviceChange = () => void apply(sinkId, "devicechange");
    md.addEventListener("devicechange", onDeviceChange);
    return () => md.removeEventListener("devicechange", onDeviceChange);
  }, [sinkId, muted, onDiagnostic]);

  const showVideo = !!stream && !cameraOff;

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden rounded-lg border bg-black transition-shadow ${
        failed
          ? "border-red-500"
          : speaking
            ? "border-emerald-400 shadow-[0_0_0_2px_rgb(52_211_153)]"
            : spotlight
              ? "border-[var(--color-primary)]"
              : "border-[var(--color-border)]"
      } ${spotlight ? "h-full w-full" : "aspect-video w-full"}`}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        className={`h-full w-full ${isScreen ? "object-contain" : "object-cover"} ${
          mirrored ? "-scale-x-100" : ""
        } ${showVideo ? "" : "hidden"}`}
      />
      {!showVideo && (
        <div className="flex flex-col items-center gap-2 text-gray-400">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white transition-colors ${
              speaking ? "bg-emerald-600" : "bg-gray-700"
            }`}
          >
            {name
              .split(" ")
              .map((p) => p[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()}
          </div>
          {cameraOff && !isScreen && <VideoOff size={14} />}
        </div>
      )}

      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
        {isScreen && <MonitorUp size={12} />}
        <span className="max-w-[10rem] truncate">{name}</span>
        {showMic &&
          (micOn ? (
            <span className="relative flex items-center" title="Mikrofon włączony">
              <Mic
                size={12}
                className={speaking ? "text-emerald-400" : "text-white/70"}
              />
              {speaking && <SpeakingBars />}
            </span>
          ) : (
            <MicOff size={12} className="text-red-400" />
          ))}
      </div>

      {playbackBlocked && !muted && (
        <button
          type="button"
          onClick={attemptPlay}
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/70"
        >
          <span className="flex items-center gap-2 rounded bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-white">
            <Volume2 size={13} /> Kliknij, aby włączyć dźwięk
          </span>
        </button>
      )}

      {(reconnecting || failed) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <span className="flex items-center gap-2 rounded bg-black/70 px-3 py-1.5 text-xs text-white">
            <RefreshCw size={13} className={failed ? "" : "animate-spin"} />
            {failed ? "Nie udało się połączyć" : "Łączenie ponownie…"}
          </span>
        </div>
      )}
    </div>
  );
}

/** Three little bars that bounce while the participant is talking. */
function SpeakingBars() {
  return (
    <span className="ml-1 flex h-3 items-end gap-[2px]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-[2px] rounded-full bg-emerald-400"
          style={{
            height: "100%",
            animation: `meetingMicBar 0.7s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </span>
  );
}
